import { Component, Inject, Input, computed, effect, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { LngLat, MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { SimulationMetrics, getMetricFromMap } from '../../../enums/simulation-metrics.enum'
import { AuthService } from '../../../../core/services/auth.service'
import { AppConfigModel } from 'src/app/core/models'
import { APP_CONFIG } from 'src/app/app.config'
import { DataSimulationWarningService } from 'src/app/shared/services/data-simulation-warning.service'
import { ToastService } from 'src/app/shared/services/toast.service'
import { SegmentSimWarningDataModel } from 'src/app/shared/models/sim-warning-creation.model'
import { MapService } from 'src/app/core/services/map.service'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { firstValueFrom } from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import * as turf from '@turf/turf'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { Directions } from '../../../enums/directions.enum'
import { SimulationWarningsEditorDataService } from '../../../services/simulation-warnings-editor-data.service'

@Component({
  selector: 'app-simulation-create-warnings-details',
  templateUrl: './simulation-create-warnings-details.component.html',
  styleUrls: ['./simulation-create-warnings-details.component.scss'],
  standalone: false,
})
export class SimulationCreateWarningsDetailsComponent {
  public allowedTargets: any = []
  private readonly _featureDetails = signal<Map<string, DetailsModel>>(
    new Map<string, DetailsModel>(),
  )
  public readonly featureDetails = this._featureDetails.asReadonly()
  public hasFeatureDetails = computed(() => this.featureDetails().size > 0)
  public metrics = SimulationMetrics
  public selectedWarningTypeMetric = this.metrics.Warnings_Accidents.toString()
  public getMetricFromMap = getMetricFromMap
  private feature: MapGeoJSONFeature | undefined
  public duration: number = 15
  public isLoading: boolean = false
  public lat: number = 0
  public lng: number = 0
  public bearing = ''
  public point: any
  public refreshIntervalId: NodeJS.Timeout | undefined
  public isAccidentsLayerVisible: boolean | undefined
  public isBdvLayerVisible: boolean | undefined
  public isTLLayerVisible: boolean | undefined
  public isCSLayerVisible: boolean | undefined
  public isPRLayerVisible: boolean | undefined
  public isWWDLayerVisible: boolean | undefined
  public isIORLayerVisible: boolean | undefined
  public isALLayerVisible: boolean | undefined
  public isBLLayerVisible: boolean | undefined
  public isSVLayerVisible: boolean | undefined
  public isTJTLayerVisible: boolean | undefined
  public onDestroy = false
  public Permissions = Permissions
  public disableButton: boolean = true
  public isDirectionAlong: boolean | undefined
  public isDirectionAgainst: boolean | undefined
  public isDirectionBoth: boolean | undefined
  public directions = Directions
  public Layers = Layers
  public confidence: number = 1.0
  public dfrs: 'DFRS_NONE' | 'DFRS_ONLY' | 'DFRS_PARTLY' = 'DFRS_NONE'
  public isAdvancedSettingsExpanded: boolean = false
  @Input() isSimulationEditorVisible: boolean = false

  constructor(
    private readonly mapEventService: MapEventsService,
    private readonly mapService: MapService,
    private readonly authService: AuthService,
    public appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private dataService: DataSimulationWarningService,
    private simulationTestLayer: LayerSimulationWarningService,
    private toastService: ToastService,
    private editorService: SimulationWarningsEditorDataService,
  ) {
    const backendScopes: any = this.authService.getUserClaims().userBackendScopes
    if (backendScopes) {
      backendScopes
        .toString()
        .split(',')
        .forEach((scope: string) => {
          this.allowedTargets.push({ name: scope, selected: false })
        })
      this.updateButtonState() // Use the new method
    }
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.feature) {
        this.evaluateQueryParameters()
      }
    })
    this.evaluateQueryParameters()

    effect(() => {
      const feature = this.mapEventService.clickedFeatures()
      this._featureDetails.set(new Map<string, DetailsModel>())
      if (feature[0]?.layer?.id === Layers.CreateSimRoadWarnings.name) {
        this.mapService.removeMarker()
        this.appState.setState(AppPaths.Simulation, {
          selectedFeatureId: undefined,
        })
        this.feature = feature[0]
        this.appState.setState(AppPaths.Simulation, {
          selectedFeatureId: feature[0].id?.toString(),
        })

        this.feature.properties['bidirectional']
          ? ((this.isDirectionAlong = true), (this.isDirectionAgainst = true))
          : ((this.isDirectionAlong = true), (this.isDirectionAgainst = false))

        // Sets direction of current segment in state
        this.appState.setState(AppPaths.Simulation, {
          selectedDirection:
            this.isDirectionAlong && this.isDirectionAgainst
              ? 'both'
              : this.isDirectionAgainst
                ? 'against'
                : 'along',
        })

        this.point = turf.nearestPointOnLine(
          this.feature?.geometry as any,
          turf.point([this.feature?.state['selectedLng'], this.feature?.state['selectedLat']]),
        )
        this.mapService.setMarker(
          {
            lng: this.point.geometry.coordinates[0],
            lat: this.point.geometry.coordinates[1],
          } as LngLat,
          `${this.selectedWarningTypeMetric}-marker`,
        )
        this.updateRoadSegmentDetail()
      }
    })
  }

  updateRoadSegmentDetail() {
    this.setFeatureDetail('RoadSegment', {
      title: 'Road Segment',
      items: [
        {
          description: 'Segment ID',
          value: this.feature?.id?.toString() ?? '',
        },
        {
          description: 'Type',
          value: this.feature?.properties['highway'],
        },
        {
          description: 'Coordinates (lng/lat)',
          value: `${this.feature?.state['selectedLng']}, ${this.feature?.state['selectedLat']}`,
        },

        {
          description: 'Bidirectional',
          value: this.feature?.properties['bidirectional'] ? 'Yes' : 'No',
        },
      ],
    })
  }

  close() {
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: undefined,
      selectedDirection: undefined,
    })
    this.feature = undefined
    this.mapService.removeMarker()
    this._featureDetails.set(new Map<string, DetailsModel>())
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: undefined,
      selectedDirection: undefined,
    })
  }

  private setFeatureDetail(key: string, value: DetailsModel): void {
    this._featureDetails.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  setMetric(warningTypeMetric: string) {
    this.selectedWarningTypeMetric = warningTypeMetric
    this.mapService.changeMarker(`${this.selectedWarningTypeMetric}-marker`)
  }

  async createWarning() {
    let newWarning: SegmentSimWarningDataModel = {
      segmentId: this.feature?.id?.toString() ?? '',
      countryCode: this.feature?.properties['iso_a2_eh'],
      warningName: getMetricFromMap(this.selectedWarningTypeMetric).name,
      latitude: this.point?.geometry.coordinates[1],
      longitude: this.point?.geometry.coordinates[0],
      ttl: this.duration * 60000,
      isCancelled: false,
      // Only include confidence if advanced settings are expanded
      ...(this.isAdvancedSettingsExpanded && {
        confidence: this.confidence,
      }),
      dfrsClassification: this.isAdvancedSettingsExpanded ? this.dfrs : 'DFRS_NONE',
      class: this.feature?.properties['highway'],
      targets: this.allowedTargets
        .filter((target: any) => target.selected)
        .map((target: any) => target.name),
      bidirectional: this.feature?.properties['bidirectional'],
      direction:
        this.isDirectionAlong && this.isDirectionAgainst
          ? 'both'
          : this.isDirectionAgainst
            ? 'against'
            : 'along',
    }
    this.isLoading = true
    if (this.isSimulationEditorVisible) {
      const rawGeometry = this.feature?.geometry as any
      const lineCoords =
        rawGeometry?.type === 'LineString' && Array.isArray(rawGeometry.coordinates)
          ? rawGeometry.coordinates
          : [
              [newWarning.longitude, newWarning.latitude],
              [newWarning.longitude, newWarning.latitude],
            ]

      this.editorService.addWarning({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: lineCoords },
        properties: newWarning,
      })
      this.toastService.show({ type: 'success', message: 'Warning added to editor' })
      this.isLoading = false
      this.close()
    } else {
      try {
        const result: any = await firstValueFrom(this.dataService.createTestWarnings(newWarning))
        this.toastService.show({ type: 'success', message: 'Warning creation initialized' })
        this.close()
        clearInterval(this.refreshIntervalId)
        this.refreshIntervalId = setInterval(() => this.simulationTestLayer.reload(), 2000)

        setTimeout(
          (function (id_local) {
            return function () {
              clearInterval(id_local)
            }
          })(this.refreshIntervalId),
          15000,
        )
      } catch (error) {
        const status = error instanceof HttpErrorResponse ? ` (${error.status})` : ''
        this.toastService.show({ type: 'error', message: `Error creating warning${status}` })
      } finally {
        this.isLoading = false
      }
    }
  }

  evaluateQueryParameters() {
    // Set the selected warning type marker based on the layers that are visible
    const layers = this.appState.getState().layers
    this.isAccidentsLayerVisible = layers?.includes(Layers.VAWarning.name)
    this.isBdvLayerVisible = layers?.includes(Layers.BDVWarning.name)
    this.isTLLayerVisible = layers?.includes(Layers.TLWarning.name)
    this.isCSLayerVisible = layers?.includes(Layers.CSWarning.name)
    this.isPRLayerVisible = layers?.includes(Layers.PRWarning.name)
    this.isWWDLayerVisible = layers?.includes(Layers.WWDWarning.name)
    this.isIORLayerVisible = layers?.includes(Layers.IORWarning.name)
    this.isALLayerVisible = layers?.includes(Layers.ALWarning.name)
    this.isBLLayerVisible = layers?.includes(Layers.BLWarning.name)
    this.isSVLayerVisible = layers?.includes(Layers.SVWarning.name)
    this.isTJTLayerVisible = layers?.includes(Layers.TJTWarning.name)

    if (
      (this.selectedWarningTypeMetric == this.metrics.Warnings_Accidents.toString() &&
        !this.isAccidentsLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_BrokenDownVehicle.toString() &&
        !this.isBdvLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_TractionLoss.toString() &&
        !this.isTLLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_ConstructionSite.toString() &&
        !this.isCSLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_PoorRoad.toString() &&
        !this.isPRLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_WrongWayDriver.toString() &&
        !this.isWWDLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_ItemOnRoad.toString() &&
        !this.isIORLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_AmberLightVehicle.toString() &&
        !this.isALLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_BlueLightVehicle.toString() &&
        !this.isBLLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_StoppedVehicle.toString() &&
        !this.isSVLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_TrafficJamTail.toString() &&
        !this.isTJTLayerVisible) ||
      this.selectedWarningTypeMetric == ''
    ) {
      if (this.isAccidentsLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_Accidents.toString()
      } else if (this.isBdvLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_BrokenDownVehicle.toString()
      } else if (this.isTLLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_TractionLoss.toString()
      } else if (this.isCSLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_ConstructionSite.toString()
      } else if (this.isPRLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_PoorRoad.toString()
      } else if (this.isWWDLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_WrongWayDriver.toString()
      } else if (this.isIORLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_ItemOnRoad.toString()
      } else if (this.isALLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_AmberLightVehicle.toString()
      } else if (this.isBLLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_BlueLightVehicle.toString()
      } else if (this.isSVLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_StoppedVehicle.toString()
      } else if (this.isTJTLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_TrafficJamTail.toString()
      } else {
        this.selectedWarningTypeMetric = ''
      }
      if (this.feature) {
        this.mapService.changeMarker(`${this.selectedWarningTypeMetric}-marker`)
      }
    }
  }

  // Add this method to extract the button state logic
  private updateButtonState(): void {
    this.allowedTargets.filter((target: any) => target.selected).length > 0
      ? (this.disableButton = false)
      : (this.disableButton = true)
  }

  toggleDirection(direction: Directions) {
    if (direction === Directions.Along) {
      this.isDirectionAlong = !this.isDirectionAlong
    }
    if (direction === Directions.Against) {
      this.isDirectionAgainst = !this.isDirectionAgainst
    }
    // Use a more explicit condition check for direction validation
    if (!this.isDirectionAgainst && !this.isDirectionAlong) {
      this.disableButton = true
    } else {
      // Check both direction and target selection
      this.updateButtonState()
    }

    let selectedDirection = ''

    if (this.isDirectionAlong && this.isDirectionAgainst) {
      selectedDirection = 'both'
    } else if (this.isDirectionAgainst) {
      selectedDirection = 'against'
    } else if (this.isDirectionAlong) {
      selectedDirection = 'along'
    }
    if (!this.isDirectionAlong && !this.isDirectionAgainst) {
      selectedDirection = 'undefined'
    }

    this.appState.setState(AppPaths.Simulation, {
      selectedDirection: selectedDirection,
    })

    // Force change detection for Angular 21
  }

  validateScopes(target: any) {
    target.selected = !target.selected
    this.updateButtonState() // Use the new method
    // Force change detection for Angular 21
  }

  // Add this method to handle rb-form-checkbox changes
  updateTargetSelection(checked: boolean, target: any): void {
    target.selected = checked
    this.updateButtonState() // Use the new method instead of calling validateScopes
    // Force change detection for Angular 21
  }

  ngOnDestroy() {
    this.mapService.removeMarker()
  }
}
